import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePropertyDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyDocumentsQuery";
import {
	ClientPropertyCategoryEnum,
	type ClientPropertyHouseDetailResponseDTO,
	ClientPropertyOwnershipEnum,
	ClientPropertyRenovationEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyDataCard } from "web_myplann/Portfolio/Components/PortfolioTools/PropertyDataCard";
import { BUILDING_OPTIONAL_PARAMS } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/buildingOptionalParams";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import {
	getBuildingMaterial,
	getDisposition,
} from "web_myplann/Portfolio/Helpers/propertyBuildingAttributes";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";

export const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyHouseDetailResponseDTO;
	currencySign: string;
	propertyId?: number;
};

export const House: FC<Props> = ({ data, currencySign, propertyId }) => {
	const { t } = useResource();

	const { data: documents } = usePropertyDocumentsQuery(propertyId);
	const { properties } = useGetPropertyOptionalParams(
		BUILDING_OPTIONAL_PARAMS,
		data,
		t,
	);

	const buildingMaterial = getBuildingMaterial(data, t);
	const disposition = getDisposition(data, t);

	const ownership =
		data?.ownership && data.ownership !== ClientPropertyOwnershipEnum.None
			? t(PageResources.Property.OwnershipValue[data.ownership])
			: "";

	const category =
		data.propertyCategory &&
		data.propertyCategory !== ClientPropertyCategoryEnum.None
			? t(PageResources.Property.CategoryValue[data.propertyCategory])
			: "";

	const renovation =
		data?.renovation && data.renovation !== ClientPropertyRenovationEnum.Null
			? t(PageResources.Property.RenovationValue[data.renovation])
			: "";

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.Category),
			value: category,
		},
		{
			label: t(PageResources.Property.StreetAndNumber),
			value: data.location,
		},
		{
			label: t(PageResources.Property.Municipality),
			value: data.locationMunicipality,
		},
		{
			label: t(PageResources.Property.ZipCode),
			value: data.locationZipCode,
		},
	];

	const propertyParametersItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.Disposition),
			value: disposition,
		},
		{
			label: t(PageResources.Property.NumberOfAboveGroundFloors),
			value: data.numberOfFloors,
		},
		{
			label: t(PageResources.Property.BuildingMaterial),
			value: buildingMaterial,
		},
		{
			label: t(PageResources.Property.FloorArea),
			value: unitFormatter(data.floorArea, UNITS.squareMeter),
		},
		{
			label: t(PageResources.Property.UsableArea),
			value: unitFormatter(data.usableArea, UNITS.squareMeter),
		},
		{
			label: t(PageResources.Property.LandArea),
			value: unitFormatter(data.landSize, UNITS.squareMeter),
		},
		{
			label: t(PageResources.Property.Ownership),
			value: ownership,
		},
		{
			label: t(PageResources.Property.Renovation),
			value: renovation,
		},
		{
			label: t(PageResources.Property.YearOfRenovation),
			value: data?.yearOfRenovation,
		},
		{
			type: "note",
			label: t(PageResources.Property.Properties),
			text: properties,
		},
	];

	const bottomComponent =
		data.purchaseDate || data.yearOfCompletion || data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<DataRow
					label={t(PageResources.Property.YearOfCompletion)}
					value={data?.yearOfCompletion}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description ?? ""}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
				items={basicInfoItems}
			/>

			<PropertyDataCard
				reproductionValue={data.reproductionValue}
				interiorPrice={data.interiorValue}
				marketValue={data.marketValue}
				priceHistory={data.estimatedPriceHistoryList}
				currencySign={currencySign}
				type={ClientPropertyTypeEnum.House}
			/>

			<DataRowCard
				preset={DataCardPreset.PropertyParameters}
				items={propertyParametersItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
